import * as moment from 'moment';

import { ReportStatus } from '@app/domain/model/report-status';
import { ReportedType } from './reported-type';
import { PurchaseReport } from '@app/feature/dealer/report/purchase-report';

/**
 * Reporting Period.
 *
 * @author Warlock, AIS.PL
 * @since 1.0
 */
export class ReportingPeriod {

  readonly citationNumbers: string[];

  readonly completion: Date;

  readonly dueDate: Date;

  readonly end: Date;

  readonly reportedTypes: Set<ReportedType>;

  readonly start: Date;

  readonly status: ReportStatus;

  readonly weekOfYear: number;

  purchaseReports: PurchaseReport[];

  // RxJS predicate for filtering only current reporting periods
  public static readonly currentOnly: (period: ReportingPeriod, index: number) => boolean =
    (period, index) => period.isCurrent()

  /**
   * Converts JSON representation of reporting period into {@link ReportingPeriod} instance.
   *
   * @param jsonObject JSON representation of reporting period
   */
  static fromJSON(jsonObject: any): ReportingPeriod {
    const result = Object.create(ReportingPeriod.prototype);
    return Object.assign(result, jsonObject, {
      completion: jsonObject.completion ? moment(jsonObject.completion).toDate() : null,
      dueDate: jsonObject.dueDate ? moment(jsonObject.dueDate).toDate() : null,
      end: jsonObject.end ? moment(jsonObject.end).toDate() : null,
      reportedTypes: jsonObject.reportedTypes ? new Set((jsonObject.reportedTypes as any[]).map(element => ReportedType.fromString(element))) : null,
      start: jsonObject.start ? moment(jsonObject.start).toDate() : null,
      status: jsonObject.status ? ReportStatus.fromString(jsonObject.status) : null,
      weekOfYear: jsonObject.start ? moment(jsonObject.start).week() : null
    });
  }

  /**
   * Verifies if this reporting period is current.
   *
   * @return true if this reporting period is current, false otherwise.
   */
  isCurrent(): boolean {
    return moment().isAfter(moment(this.start));
  }

  /**
   * @return lastDay day of this reporting period
   */
  lastDay(): Date {
    return moment(this.end).subtract(1, 'day').toDate();
  }

  /**
   * @return boolean array of days enclosed by this reporting period
   */
  days(): Date[] {
    const mStart = moment(this.start);
    const mEnd = moment(this.end);
    return Array
      .from({ length: mEnd.diff(mStart, 'day') }, (v, k) => moment(mStart).add(k, 'day'))
      .map(m => m.toDate());
  }

}
